import React, { useState } from 'react';

import {
  BaseProgressPercentage,
  BaseProgressTrackerWrapper,
} from 'components/wrappers/BaseProgressBar';
import { calculatePercent } from 'utils/trackerUtils';
import { StepValidationStatuses } from 'enums';
import BaseBulkImportStep, {
  BulkImportStagesModel,
} from 'components/wrappers/BaseBulkImportStep';
import AssignmentUploadSection from './AssignmentUploadSection';
import AssignmentBulkImportTableSection from './AssignmentBulkImportTableSection';
import {
  AssignmentBulkImportViewModel,
  BulkAssignmentImportResponse,
} from 'api/adminApiClient';
import { bulkImportAssignments } from 'redux/reducers/assignmentSlice';
import { useAppDispatch } from 'hooks/useAppDispatch';
import BulkImportResultModal, {
  BulkImportMode,
} from 'components/modals/BulkImportResultModal';
import { mapValuesToBulkImportResult } from 'utils/bulkImportUtils';
import { addIdsToValues } from 'utils/arrayUtils';
import { getListOfAssignmentsWithWorkerFile } from 'utils/assignmentUtils';

import styles from './AssignmentBulkImportPage.module.less';

export interface AssignmentBulkImportModel
  extends AssignmentBulkImportViewModel {
  id?: string;
}

const initialStages: BulkImportStagesModel[] = [
  {
    title: 'Upload file',
    status: StepValidationStatuses.Process,
    stepIndex: 1,
    prefixCls: 'ant-steps',
    iconPrefix: 'ant',
  },
  {
    title: 'File Processing',
    status: StepValidationStatuses.Wait,
    stepIndex: 2,
    prefixCls: 'ant-steps',
    iconPrefix: 'ant',
  },
  {
    title: 'Complete import',
    status: StepValidationStatuses.Wait,
    stepIndex: 3,
    prefixCls: 'ant-steps',
    iconPrefix: 'ant',
  },
];

const BATCH_SIZE = 3;

export default function AssignmentBulkImportPage() {
  const [isAssignmentBulkImportModal, setIsAssignmentBulkImportModal] =
    useState(false);
  const [stages, setStages] = useState(initialStages);
  const [assignments, setAssignments] = useState<AssignmentBulkImportModel[]>(
    [],
  );
  const [responseDetails, setResponseDetails] = useState<
    BulkAssignmentImportResponse[]
  >([]);
  const dispatch = useAppDispatch();

  const listOfAssignmentsWithWorkerFile =
    getListOfAssignmentsWithWorkerFile(assignments);

  const isUploadSection =
    stages[0].status === StepValidationStatuses.Process ||
    listOfAssignmentsWithWorkerFile.length === 0;

  const onSetAssignments = async (
    assignments: AssignmentBulkImportViewModel[],
  ) => {
    const updatedListOfAssignments = addIdsToValues(assignments);

    const listOfAssignmentsWithWorkerFile = getListOfAssignmentsWithWorkerFile(
      updatedListOfAssignments,
    );

    if (!listOfAssignmentsWithWorkerFile.length) {
      onSetStageStatus(0);
      return await handleSubmit(assignments);
    }

    setAssignments(updatedListOfAssignments);
    onSetStageStatus(0);
  };

  const onSetStageStatus = (changeStageIndex: number) => {
    setStages((prevState) => {
      return prevState.map((stage, index) => {
        if (index === changeStageIndex)
          return { ...stage, status: StepValidationStatuses.Finish };

        if (index === changeStageIndex + 1)
          return { ...stage, status: StepValidationStatuses.Process };

        return stage;
      });
    });
  };

  const handleAssignmentBulkImportOpen = () => {
    setIsAssignmentBulkImportModal(true);
  };

  const handleAssignmentBulkImportClose = () => {
    setIsAssignmentBulkImportModal(false);
    setStages(initialStages);
  };

  const handleSubmit = async (values: AssignmentBulkImportViewModel[]) => {
    onSetStageStatus(1);
    const responseDetails: BulkAssignmentImportResponse[] = [];
    const parts = splitArrayIntoParts(values);

    for (let i = 0; i < parts.length; i++) {
      const batch = parts[i];

      const result = await dispatch(bulkImportAssignments(batch));

      if (bulkImportAssignments.fulfilled.match(result)) {
        responseDetails.push(...result.payload);
        await new Promise((resolve) => setTimeout(resolve, 1000));
      }
    }

    setResponseDetails(responseDetails);
    handleAssignmentBulkImportOpen();
    onSetStageStatus(2);
  };

  const splitArrayIntoParts = (
    assignments: AssignmentBulkImportViewModel[],
  ) => {
    const parts = [];

    for (let i = 0; i < assignments.length; i += BATCH_SIZE) {
      parts.push(assignments.slice(i, i + BATCH_SIZE));
    }

    return parts;
  };

  return (
    <>
      {isAssignmentBulkImportModal && (
        <BulkImportResultModal
          onClose={handleAssignmentBulkImportClose}
          results={mapValuesToBulkImportResult(responseDetails)}
          mode={BulkImportMode.Assignment}
        />
      )}
      <div className="page-wrapper scroll-box">
        <div className="page-holder">
          <div className={styles.holder}>
            <div className={styles.progressTracker}>
              <div className={styles.progressPercentage}>
                <BaseProgressPercentage percent={calculatePercent(stages)} />
              </div>

              <div className="page-box">
                <div className="page-inner-container">
                  <BaseProgressTrackerWrapper>
                    {stages.map((stage, index) => (
                      <BaseBulkImportStep
                        key={index}
                        stage={stage}
                        index={index}
                      />
                    ))}
                  </BaseProgressTrackerWrapper>
                </div>
              </div>
            </div>
          </div>

          {isUploadSection && (
            <AssignmentUploadSection onSuccess={onSetAssignments} />
          )}

          {!isUploadSection && (
            <AssignmentBulkImportTableSection
              assignments={assignments}
              onSubmit={handleSubmit}
            />
          )}
        </div>
      </div>
    </>
  );
}
